import { BLUETOOTH_DC_REASONS } from "./bluetoothConstants";

// Thrown by connectAndSetup() when disconnect() ends the pairing before setup finished.
export class SetupCancelledError extends Error {
  constructor() {
    super("Connection setup cancelled");
    this.name = "SetupCancelledError";
  }
}

export class BluetoothManager {
  device?: BluetoothDevice;
  server?: BluetoothRemoteGATTServer;

  characteristics = new Map<string, BluetoothRemoteGATTCharacteristic>();
  listeners = new Map<string, Function[]>();
  // The "characteristicvaluechanged" handler attached to each notifying characteristic, so a
  // repeated setup (after a reconnect) replaces it instead of adding a second one.
  private notificationHandlers = new Map<
    string,
    {
      characteristic: BluetoothRemoteGATTCharacteristic;
      handler: (event: Event) => void;
    }
  >();

  onDisconnect?: (reason: number) => void;

  connected = false;
  // True once services are set up. A link that drops before then is retried by
  // connectAndSetup(); only a link that drops afterwards is reconnected in the background.
  ready = false;
  private setupTables?: {
    services: Record<string, string>;
    characteristics: Record<string, Record<string, string>>;
  };
  reconnectAttempts = 0;
  maxReconnectAttempts = 5;
  // Waits between setup attempts. After the page disconnects, Windows keeps the radio link up
  // for a few seconds (2.5-5 s measured against a trap); a connect in that window attaches to
  // the dying link and is dropped when Windows closes it. The waits add up to 10 s so the
  // retries outlast that window, and a reconnect only fails when the trap is out of reach.
  setupRetryDelaysMs = [1000, 2000, 3000, 4000];
  get maxSetupAttempts() {
    return this.setupRetryDelaysMs.length + 1;
  }
  // Bumped by disconnect(). Work started under an older value belongs to a pairing that has
  // ended, even when the next pairing got the same BluetoothDevice back from Chrome.
  private session = 0;

  // Throws once disconnect() has ended the pairing `session` belongs to, first closing any link
  // that pairing's connect opened, unless a newer pairing is using the same device.
  private checkSession(session: number, device?: BluetoothDevice) {
    if (session === this.session) return;
    if (device !== this.device) device?.gatt?.disconnect();
    throw new SetupCancelledError();
  }

  private withTimeout<T>(
    promise: Promise<T>,
    ms: number,
    label: string,
  ): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`Timeout: ${label} took longer than ${ms}ms`)),
          ms,
        );
      }),
    ]).finally(() => clearTimeout(timer));
  }

  checkSupportBLE(): boolean {
    return !!navigator?.bluetooth;
  }

  setOnDisconnectCallback(callback: (reason: number) => void) {
    this.onDisconnect = callback;
  }

  async requestDevice(
    services: Record<string, string>,
    filters: BluetoothLEScanFilter[],
  ) {
    console.log("Requesting Bluetooth Device...");
    const serviceUUIDs = Object.values(services);

    this.device = await navigator.bluetooth.requestDevice({
      filters,
      optionalServices: serviceUUIDs,
    });

    this.device.addEventListener(
      "gattserverdisconnected",
      this.handleDisconnect,
    );

    return this.device;
  }

  async connect(session: number) {
    console.log("Connecting to device...");
    const device = this.device;
    if (!device) throw new Error("No device selected");

    const server = await device.gatt!.connect();
    this.checkSession(session, device);
    this.server = server;
    this.reconnectAttempts = 0;
    this.connected = true;

    // small delay to ensure connection is fully established before proceeding with setup
    await new Promise((resolve) => setTimeout(resolve, 500));
    this.checkSession(session, device);
  }

  // Connects and sets up services, starting over when the link drops part-way. On Windows this
  // happens when connecting again shortly after a disconnect (see setupRetryDelaysMs), and a
  // page refresh used to be the only way past it.
  async connectAndSetup(
    services: Record<string, string>,
    characteristics: Record<string, Record<string, string>>,
  ) {
    const session = this.session;
    const device = this.device;
    this.setupTables = { services, characteristics };
    for (let attempt = 1; ; attempt++) {
      try {
        await this.connect(session);
        await this.setupServices(services, characteristics, session);
        this.ready = true;
        return;
      } catch (error) {
        this.checkSession(session, device);
        if (attempt >= this.maxSetupAttempts) {
          throw error;
        }
        console.warn(
          `Connection setup failed (attempt ${attempt} of ${this.maxSetupAttempts}), retrying: ${error}`,
        );
        await new Promise((resolve) =>
          setTimeout(resolve, this.setupRetryDelaysMs[attempt - 1]),
        );
        this.checkSession(session, device);
      }
    }
  }

  async setupServices(
    services: Record<string, string>,
    characteristics: Record<string, Record<string, string>>,
    session: number,
  ) {
    if (!this.server) throw new Error("Not connected");
    const device = this.device;

    console.log("Setting up services...");

    for (const [serviceName, serviceUUID] of Object.entries(services)) {
      console.log(`Setting up service ${serviceName}`);
      const service = await this.withTimeout(
        this.server.getPrimaryService(serviceUUID),
        10000,
        `getPrimaryService(${serviceName})`,
      );
      this.checkSession(session, device);

      const serviceChars = characteristics[serviceName];

      for (const [charName, charUUID] of Object.entries(serviceChars)) {
        console.log(`Setting up characteristic ${charName}`);
        const char = await this.withTimeout(
          service.getCharacteristic(charUUID),
          10000,
          `getCharacteristic(${serviceName}.${charName})`,
        );
        this.checkSession(session, device);

        const key = `${serviceName}.${charName}`;

        this.characteristics.set(key, char);

        if (char.properties.notify || char.properties.indicate) {
          console.log(`Starting notifications for ${key}`);
          await char.startNotifications();
          this.checkSession(session, device);
          console.log(`Subscribed to notifications for ${key}`);
          const previous = this.notificationHandlers.get(key);
          if (previous) {
            previous.characteristic.removeEventListener(
              "characteristicvaluechanged",
              previous.handler,
            );
          }
          const handler = (event: Event) => {
            const target = event.target as BluetoothRemoteGATTCharacteristic;
            this.emit(key, target?.value || null);
          };
          char.addEventListener("characteristicvaluechanged", handler);
          this.notificationHandlers.set(key, { characteristic: char, handler });
        }
      }
    }
    console.log("All services set up");
  }

  async write(service: string, char: string, data: Uint8Array) {
    const key = `${service}.${char}`;

    const characteristic = this.characteristics.get(key);

    if (!characteristic) {
      throw new Error(`Characteristic ${key} not found`);
    }

    await characteristic.writeValue(data as BufferSource);
  }

  async writeStr(service: string, char: string, data: string) {
    const key = `${service}.${char}`;

    const characteristic = this.characteristics.get(key);

    if (!characteristic) {
      throw new Error(`Characteristic ${key} not found`);
    }

    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);

    await characteristic.writeValue(encodedData as BufferSource);
  }

  private async readBuff(service: string, char: string, timeout: number = 0) {
    const key = `${service}.${char}`;

    const characteristic = this.characteristics.get(key);

    if (!characteristic) {
      throw new Error(`Characteristic ${key} not found`);
    }

    let buf;
    if (timeout > 0) {
      buf = await this.withTimeout(
        characteristic.readValue(),
        timeout,
        `readBuff${service}-${char}`,
      );
    } else {
      buf = await characteristic.readValue();
    }

    return buf;
  }

  async read(service: string, char: string, timeout: number = 0) {
    const buf = await this.readBuff(service, char, timeout);
    const decoder = new TextDecoder();
    return decoder.decode(buf);
  }

  async readBool(service: string, char: string, timeout: number = 0) {
    const buf = await this.readBuff(service, char, timeout);

    const bytes = new Uint8Array(buf.buffer);
    return bytes[0] === 1 ? true : false;
  }

  subscribe(service: string, char: string, cb: Function) {
    const key = `${service}.${char}`;

    if (!this.listeners.has(key)) {
      this.listeners.set(key, []);
    }

    this.listeners.get(key)!.push(cb);
  }

  emit(key: string, value: DataView | null) {
    const subs = this.listeners.get(key) || [];

    subs.forEach((cb) => cb(value));
  }

  // A link that drops after setup is reconnected and set up again: reconnecting alone leaves
  // the old notification subscriptions dead, and the trap's results would never arrive.
  handleDisconnect = async () => {
    console.warn("BLE disconnected");
    this.connected = false;
    if (!this.ready || !this.setupTables) {
      // Still setting up: connectAndSetup() retries.
      return;
    }
    this.ready = false;
    const session = this.session;
    const { services, characteristics } = this.setupTables;

    while (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      try {
        await this.connect(session);
        await this.setupServices(services, characteristics, session);
        this.ready = true;
        this.reconnectAttempts = 0;
        console.log("Reconnected");
        return;
      } catch (err) {
        if (session !== this.session) return;
        console.warn(
          `Reconnect attempt ${this.reconnectAttempts} failed: ${err}`,
        );
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (session !== this.session) return;
      }
    }
    this.disconnect(BLUETOOTH_DC_REASONS.UNKNOWN);
  };

  disconnect(reason: number = BLUETOOTH_DC_REASONS.UNKNOWN) {
    console.log("Disconnecting from device...");
    // Detach first, so the gattserverdisconnected event from the gatt.disconnect() below does
    // not start a background reconnect. Bumping this.session also stops one already running,
    // and a connectAndSetup() still retrying.
    this.session++;
    this.device?.removeEventListener(
      "gattserverdisconnected",
      this.handleDisconnect,
    );
    if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect();
    }
    this.connected = false;
    this.ready = false;
    this.setupTables = undefined;
    this.reconnectAttempts = 0;
    this.device = undefined;
    this.server = undefined;
    for (const {
      characteristic,
      handler,
    } of this.notificationHandlers.values()) {
      characteristic.removeEventListener("characteristicvaluechanged", handler);
    }
    this.notificationHandlers.clear();
    this.characteristics.clear();
    this.listeners.clear();
    if (this.onDisconnect) {
      this.onDisconnect(reason);
    }
    console.log("Disconnected");
  }
}
